/**
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Material Kit 2 React.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Material Kit 2 React using thie file.
 */

// Material Kit 2 React Base Styles
import colors from "assets/theme/base/colors";

// Material Kit 2 React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";

const { dark } = colors;

const baseProperties = {
  // fontFamily: "'Baloo 2', sans-serif",
  // fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  // fontFamily2: "'Baloo 2', sans-serif",
  fontFamily: "'Fredoka One', cursive",
  fontFamily2: "'Luckiest Guy', cursive",
  // fontFamily: "'Bangers', cursive",
  // fontFamily2: "'Bangers', cursive",
  // fontFamily2: '"Roboto Slab", sans-serif',
  fontWeightLighter: 100,
  fontWeightLight: 200,
  fontWeightRegular: 300,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(40),
};

export function responsiveFontSizes({ xs, sm, md, lg }) {
  return {
    "@media (min-width:0px)": {
      fontSize: pxToRem(xs),
    },
    "@media (min-width:600px)": {
      fontSize: pxToRem(sm),
    },
    "@media (min-width:900px)": {
      fontSize: pxToRem(md),
    },
    "@media (min-width:1200px)": {
      fontSize: pxToRem(lg),
    },
  };
}

const cartoonStyle = {
  textShadow: "1px 1px black",
  letterSpacing: "2px !important",
};

const cartoonStyleL = {
  textShadow: "2px 2px black",
  letterSpacing: "3px !important",
};

const baseHeadingProperties = {
  color: dark.main,
  fontWeight: baseProperties.fontWeightBold,
};

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
};

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLighter: baseProperties.fontWeightLighter,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(60),
    lineHeight: 1.25,
    ...cartoonStyleL,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },

  h2: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(36),
    lineHeight: 1.3,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ xs: 24, sm: 32, md: 34, lg: 36 }),
  },

  h3: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(30),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ xs: 20 , sm: 26, md: 30, lg: 30 }),
  },

  h4: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(24),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ xs: 19, sm: 20, md: 24, lg: 24 }),
  },

  h5: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(20),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ xs: 16, sm: 16, md: 20, lg: 20 }),
  },

  h6: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(16),
    lineHeight: 1.625,
    ...baseHeadingProperties,
    ...responsiveFontSizes({ sm: 16, md: 16, lg: 16 }),
  },

  subtitle1: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.625,
    ...responsiveFontSizes({ xs: 18, sm: 20, md: 22, lg: 22 }),
  },

  subtitle2: {
    ...cartoonStyleL,
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
    ...responsiveFontSizes({ xs: 18, sm: 20, md: 22, lg: 22 }),
  },

  body1: {
    ...cartoonStyle,
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
    letterSpacing: 1.35,
    ...responsiveFontSizes({
      xs: baseProperties.fontSizeXL,
      sm: baseProperties.fontSizeXL,
      md: baseProperties.fontSizeXL,
      lg: baseProperties.fontSizeXL,
    }),
  },

  body2: {
    ...cartoonStyle,
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
    ...responsiveFontSizes({ xs: 16, sm: 16, md: 16, lg: 16 }),
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightLighter,
    lineHeight: 1.5,
    textTransform: "uppercase",
    letterSpacing: 1.25,
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.15,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    "2xl": baseProperties.fontSize2XL,
    "3xl": baseProperties.fontSize3XL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
};

export default typography;
